import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { RootState } from '../store';
import { setCredentials } from '../store/authSlice';
import { useUpdateMeMutation, useChangePasswordMutation } from '../store/authApi';

const Profile: React.FC = () => {
  const { user, token } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const [updateMe, { isLoading: savingProfile }] = useUpdateMeMutation();
  const [changePassword, { isLoading: savingPassword }] = useChangePasswordMutation();

  const [profile, setProfile] = useState({ name: '', designation: '' });
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || '', designation: user.designation || '' });
    }
  }, [user]);

  const initials = user?.name
    ? user.name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('')
    : 'U';

  const roleLabel = user?.role === 'superadmin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User';
  const roleCls = user?.role === 'superadmin' ? 'badge-purple' : user?.role === 'admin' ? 'badge-orange' : 'badge-blue';

  const profileChanged = profile.name !== (user?.name || '') || profile.designation !== (user?.designation || '');

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile.name.trim()) return toast.error('Name is required');
    if (!profile.designation.trim()) return toast.error('Designation is required');

    try {
      const updated = await updateMe(profile).unwrap();
      if (token) dispatch(setCredentials({ user: updated, token }));
      toast.success('Profile updated');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw.current) return toast.error('Enter your current password');
    if (pw.next.length < 6) return toast.error('New password must be at least 6 characters');
    if (pw.next !== pw.confirm) return toast.error('New passwords do not match');

    try {
      await changePassword({ currentPassword: pw.current, newPassword: pw.next }).unwrap();
      toast.success('Password changed successfully');
      setPw({ current: '', next: '', confirm: '' });
    } catch (e: any) {
      toast.error(e?.data?.message || 'Failed to change password');
    }
  };

  const labelCls = 'block text-xs font-display font-semibold text-trident-gray-600 uppercase tracking-wider mb-1.5';

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-trident-gray-900">Profile Settings</h2>
        <p className="text-sm text-trident-gray-500">Manage your personal information and password.</p>
      </div>

      {/* Profile header card */}
      <div className="bg-white rounded-2xl border border-trident-gray-100 shadow-card p-6 mb-5">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-trident-gradient flex items-center justify-center text-white font-display font-extrabold text-2xl shadow-trident-lg flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-xl text-trident-gray-900 truncate">
              {user?.name || 'Your Account'}
            </h3>
            <div className="text-sm text-trident-gray-500 truncate">{user?.email}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={roleCls}>{roleLabel}</span>
              {user?.isVerified && (
                <span className="badge-green inline-flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Verified
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Edit Profile */}
        <div className="bg-white rounded-2xl border border-trident-gray-100 shadow-card p-6">
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-1">Personal Information</h3>
          <p className="text-xs text-trident-gray-500 mb-5">Update your name and designation.</p>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={user?.email || ''} disabled className="input-field bg-trident-gray-50 cursor-not-allowed" />
              <p className="text-xs text-trident-gray-400 mt-1">Email cannot be changed. Contact an admin if needed.</p>
            </div>
            <div>
              <label className={labelCls}>Full Name</label>
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="input-field"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className={labelCls}>Designation</label>
              <input
                type="text"
                value={profile.designation}
                onChange={(e) => setProfile({ ...profile, designation: e.target.value })}
                className="input-field"
                placeholder="e.g., QA Engineer"
              />
            </div>
            <button
              type="submit"
              disabled={!profileChanged || savingProfile}
              className="btn-primary w-full justify-center flex items-center gap-2"
            >
              {savingProfile ? 'Saving...' : profileChanged ? 'Save Changes' : 'No Changes'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-2xl border border-trident-gray-100 shadow-card p-6">
          <h3 className="font-display font-bold text-lg text-trident-gray-900 mb-1">Change Password</h3>
          <p className="text-xs text-trident-gray-500 mb-5">
            Use a strong password you don't use elsewhere.
          </p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {(['current', 'next', 'confirm'] as const).map((field) => {
              const labels = { current: 'Current Password', next: 'New Password', confirm: 'Confirm New Password' };
              const placeholders = { current: 'Enter current password', next: 'Min 6 characters', confirm: 'Retype new password' };
              return (
                <div key={field}>
                  <label className={labelCls}>{labels[field]}</label>
                  <div className="relative">
                    <input
                      type={showPw[field] ? 'text' : 'password'}
                      value={pw[field]}
                      onChange={(e) => setPw({ ...pw, [field]: e.target.value })}
                      className="input-field pr-11"
                      placeholder={placeholders[field]}
                      autoComplete={field === 'current' ? 'current-password' : 'new-password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw({ ...showPw, [field]: !showPw[field] })}
                      className="absolute top-1/2 right-4 -translate-y-1/2 text-trident-gray-400 hover:text-trident-orange transition"
                      tabIndex={-1}
                    >
                      {showPw[field] ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
            <button
              type="submit"
              disabled={savingPassword || !pw.current || !pw.next || !pw.confirm}
              className="btn-primary w-full justify-center flex items-center gap-2"
            >
              {savingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
